import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { DICT } from "../../lib/i18n";
import { IMAGES } from "../../lib/data";
import { useContent, resolveImg } from "../../context/ContentContext";
import { AdminSettings } from "./AdminSettings";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Groupes de textes (basés sur les clés du dictionnaire i18n) + email de bienvenue
// ---------------------------------------------------------------------------
const GROUP_LABELS = {
  general: "Général & bandeau d'annonce",
  nav: "Navigation",
  common: "Boutons communs",
  hero: "Accueil — Hero",
  why: "Accueil — Pourquoi nous choisir",
  results: "Accueil — Résultats",
  offer: "Accueil — Offre",
  footer: "Pied de page",
  cart: "Panier & commande",
  field: "Champs de formulaire",
  product: "Page produit",
  formule: "Page formule",
  marque: "Page Notre histoire",
  rituel: "Page Rituel",
  coffrets: "Page Coffrets",
  contact: "Page Contact",
  stock: "Badges de stock",
};

const EMAIL_ITEMS = [
  { key: "email.welcome.subject", fr: "Bienvenue chez LUXE ORYVEA ✨", en: "Welcome to LUXE ORYVEA ✨" },
  { key: "email.welcome.title", fr: "Bienvenue, [Prénom]", en: "Welcome, [First name]" },
  { key: "email.welcome.intro", fr: "Votre compte a bien été créé. Vous avez désormais accès à votre espace personnel pour suivre vos commandes et profiter du rituel LUXE ORYVEA.", en: "Your account has been created successfully. You now have access to your personal space to track your orders and enjoy the LUXE ORYVEA ritual." },
];

function buildTextGroups() {
  const groups = {};
  Object.keys(DICT.fr).forEach((key) => {
    const prefix = key.includes(".") ? key.split(".")[0] : "general";
    (groups[prefix] = groups[prefix] || []).push({ key, fr: DICT.fr[key], en: DICT.en[key] || "" });
  });
  const ordered = [];
  Object.keys(GROUP_LABELS).forEach((p) => { if (groups[p]) ordered.push([p, groups[p]]); });
  Object.keys(groups).forEach((p) => { if (!GROUP_LABELS[p]) ordered.push([p, groups[p]]); });
  ordered.push(["email", EMAIL_ITEMS]);
  return ordered;
}

const isLong = (s) => (s || "").length > 70;

function TextEditor() {
  const { refresh } = useContent();
  const [over, setOver] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const groups = useMemo(buildTextGroups, []);

  useEffect(() => { api.get("/admin/content").then((r) => setOver(r.data?.texts || {})).catch(() => setOver({})); }, []);

  const set = (key, lang, val) =>
    setOver((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [lang]: val } }));

  const resetTexts = async () => {
    if (!window.confirm("Réinitialiser tous les textes aux textes d'origine du site ?")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/content/reset-texts");
      setOver(data?.texts || {});
      refresh();
      toast.success("Textes réinitialisés aux textes d'origine");
    } catch { toast.error("Erreur lors de la réinitialisation des textes"); }
    finally { setResetting(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      Object.entries(over || {}).forEach(([k, v]) => {
        const fr = (v?.fr || "").trim();
        const en = (v?.en || "").trim();
        if (fr || en) payload[k] = { fr, en };
      });
      await api.put("/admin/content", { texts: payload });
      refresh();
      toast.success("Textes du site enregistrés");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setBusy(false); }
  };

  if (!over) return <p className="text-sm" style={{ color: "var(--lo-muted)" }}>Chargement…</p>;

  const q = query.trim().toLowerCase();

  return (
    <div data-testid="admin-content-texts">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un texte…"
          className="bg-transparent border-b py-2 text-sm outline-none flex-1 min-w-[200px]" style={{ borderColor: "var(--lo-border)" }} data-testid="content-text-search" />
        <button onClick={resetTexts} disabled={busy || resetting} className="btn-ghost h-11 px-5 disabled:opacity-50" data-testid="content-text-reset">{resetting ? "…" : "Réinitialiser aux textes d'origine"}</button>
        <button onClick={save} disabled={busy || resetting} className="btn-primary h-11 px-8 disabled:opacity-50" data-testid="content-text-save">{busy ? "…" : "Enregistrer les textes"}</button>
      </div>
      <p className="text-xs mb-6" style={{ color: "var(--lo-muted)" }}>
        Laissez un champ vide pour conserver le texte par défaut (affiché en gris). Modifiez le FR et/ou l'EN : le site public se met à jour au même endroit, avec le même design.
      </p>
      <div className="space-y-3">
        {groups.map(([prefix, items]) => {
          const filtered = q
            ? items.filter((it) => it.key.toLowerCase().includes(q) || (it.fr || "").toLowerCase().includes(q) || (it.en || "").toLowerCase().includes(q))
            : items;
          if (filtered.length === 0) return null;
          return (
            <details key={prefix} className="card-lux p-0 overflow-hidden" open={!!q}>
              <summary className="cursor-pointer select-none px-6 py-4 overline" style={{ fontSize: "0.62rem" }} data-testid={`content-group-${prefix}`}>
                {GROUP_LABELS[prefix] || (prefix === "email" ? "Email de bienvenue" : prefix)} · {filtered.length}
              </summary>
              <div className="px-6 pb-6 space-y-5 border-t" style={{ borderColor: "var(--lo-border)" }}>
                {filtered.map((it) => {
                  const val = over[it.key] || {};
                  const Field = isLong(it.fr) ? "textarea" : "input";
                  return (
                    <div key={it.key} className="pt-4">
                      <span className="block mb-2 text-[10px] tracking-wider" style={{ color: "var(--lo-rose-hi)" }}>{it.key}</span>
                      <div className="grid md:grid-cols-2 gap-4">
                        <label className="block">
                          <span className="overline block mb-1" style={{ fontSize: "0.52rem" }}>Français</span>
                          <Field rows={2} value={val.fr ?? ""} placeholder={it.fr} onChange={(e) => set(it.key, "fr", e.target.value)}
                            className="w-full bg-transparent border py-2 px-3 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid={`text-fr-${it.key}`} />
                        </label>
                        <label className="block">
                          <span className="overline block mb-1" style={{ fontSize: "0.52rem" }}>English</span>
                          <Field rows={2} value={val.en ?? ""} placeholder={it.en} onChange={(e) => set(it.key, "en", e.target.value)}
                            className="w-full bg-transparent border py-2 px-3 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid={`text-en-${it.key}`} />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          );
        })}
      </div>
      <button onClick={save} disabled={busy} className="btn-primary h-11 px-8 mt-8 disabled:opacity-50" data-testid="content-text-save-bottom">{busy ? "…" : "Enregistrer les textes"}</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Éditeur d'images (mêmes clés que data.js IMAGES)
// ---------------------------------------------------------------------------
const IMAGE_GROUPS = [
  { title: "Résultats — Homme", keys: [["hommeResult1", "Résultat Homme 1"], ["hommeResult2", "Résultat Homme 2"], ["hommeResult3", "Résultat Homme 3"]] },
  { title: "Résultats — Femme", keys: [["femmeResult1", "Résultat Femme 1"], ["femmeResult2", "Résultat Femme 2"]] },
  { title: "Flacons (Hero)", keys: [["heroBottle", "Flacon Homme"], ["femmeBottle", "Flacon Femme"]] },
  { title: "Section Ingrédients", keys: [["dropper", "Pipette Homme"], ["femmeDropper", "Pipette Femme"], ["ingredientHoney", "Photo Miel"], ["ingredientCaffeine", "Photo Caféine"], ["ingredientNiacinamide", "Photo Niacinamide"], ["ingredientArgan", "Photo Argan"]] },
  { title: "Offre & Coffrets", keys: [["offer", "Offre Homme"], ["femmeOffer", "Offre Femme"], ["packaging", "Photo Duo Homme"], ["femmeDuo", "Photo Duo Femme"], ["coffrets", "Bannière Coffrets"], ["coffretMixte", "Photo Coffret Mixte"], ["coffretFemme", "Photo Coffret Femme"], ["coffretHomme", "Photo Coffret Homme"]] },
  { title: "Galerie produit", keys: [["hommeBoard", "Planche Homme"], ["femmeBoard", "Planche Femme"], ["texture", "Texture"], ["hommeRitual", "Rituel Homme"], ["femmeRitual", "Rituel Femme"]] },
  { title: "Pages Histoire & Rituel", keys: [["marque", "Photo Page Notre histoire"], ["ritual", "Bannière Rituel"]] },
];

async function optimizeImageForWeb(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml") return file;

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = objectUrl;
    });

    const MAX_SIDE = 1600;
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
    const width = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
    const height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, width, height);

    // WebP gives a much smaller file while remaining visually very clean.
    // Lower quality only as much as needed to keep CMS images light.
    const qualities = [0.88, 0.84, 0.80, 0.76, 0.72];
    const MAX_BYTES = 450 * 1024;
    let best = null;
    for (const quality of qualities) {
      const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/webp", quality));
      if (!blob) continue;
      best = blob;
      if (blob.size <= MAX_BYTES) break;
    }

    if (!best || best.size >= file.size) return file;
    const baseName = (file.name || "image").replace(/\.[^.]+$/, "");
    return new File([best], `${baseName}.webp`, { type: "image/webp", lastModified: Date.now() });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function ImageRow({ imgKey, label, current, onChange }) {
  const [uploading, setUploading] = useState(false);
  const preview = current ? resolveImg(current) : IMAGES[imgKey];
  const onFile = async (e) => {
    const original = e.target.files?.[0];
    if (!original) return;
    setUploading(true);
    try {
      const file = await optimizeImageForWeb(original);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      const savedKb = Math.round(file.size / 1024);
      const originalKb = Math.round(original.size / 1024);
      toast.success(savedKb < originalKb ? `Photo optimisée : ${originalKb} Ko → ${savedKb} Ko` : "Photo téléversée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-4 py-3 border-b" style={{ borderColor: "var(--lo-border)" }} data-testid={`image-row-${imgKey}`}>
      <img src={preview} alt={label} className="w-16 h-16 object-cover shrink-0" style={{ border: "1px solid var(--lo-border)" }} />
      <div className="flex-1 min-w-0">
        <p className="text-sm" style={{ color: "var(--lo-text)" }}>{label}</p>
        <p className="text-[10px] truncate" style={{ color: "var(--lo-muted)" }}>{current ? "Personnalisée" : "Image par défaut"}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {current && (
          <button onClick={() => onChange("")} className="btn-ghost h-9 px-3 text-xs" data-testid={`image-reset-${imgKey}`}>Réinitialiser</button>
        )}
        <label className="btn-ghost h-9 px-4 text-xs cursor-pointer whitespace-nowrap" data-testid={`image-upload-${imgKey}`}>
          {uploading ? "…" : "Remplacer"}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
        </label>
      </div>
    </div>
  );
}

function ImageEditor() {
  const { refresh } = useContent();
  const [over, setOver] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/content").then((r) => setOver(r.data?.images || {})).catch(() => setOver({})); }, []);

  const set = (key, val) => setOver((prev) => {
    const next = { ...prev };
    if (val) next[key] = val; else delete next[key];
    return next;
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/content", { images: over || {} });
      refresh();
      toast.success("Images du site enregistrées");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setBusy(false); }
  };

  if (!over) return <p className="text-sm" style={{ color: "var(--lo-muted)" }}>Chargement…</p>;

  return (
    <div data-testid="admin-content-images">
      <div className="flex justify-end mb-6">
        <button onClick={save} disabled={busy} className="btn-primary h-11 px-8 disabled:opacity-50" data-testid="content-image-save">{busy ? "…" : "Enregistrer les images"}</button>
      </div>
      <p className="text-xs mb-6" style={{ color: "var(--lo-muted)" }}>
        Téléversez une nouvelle photo pour remplacer une image du site. Les dimensions et l'emplacement du design sont conservés automatiquement. « Réinitialiser » remet l'image d'origine.
      </p>
      <div className="space-y-5">
        {IMAGE_GROUPS.map((g) => (
          <div key={g.title} className="card-lux p-6">
            <p className="overline mb-2" style={{ fontSize: "0.6rem" }}>{g.title}</p>
            {g.keys.map(([k, label]) => (
              <ImageRow key={k} imgKey={k} label={label} current={over[k]} onChange={(v) => set(k, v)} />
            ))}
          </div>
        ))}
      </div>
      <button onClick={save} disabled={busy} className="btn-primary h-11 px-8 mt-8 disabled:opacity-50" data-testid="content-image-save-bottom">{busy ? "…" : "Enregistrer les images"}</button>
    </div>
  );
}

function VideoEditor() {
  const { videosRaw, refresh } = useContent();
  const [over, setOver] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/content").then((r) => setOver(r.data?.videos || {})).catch(() => setOver({}));
  }, []);

  const current = over?.heroHome || "";
  const preview = current ? resolveImg(current) : "/assets/luxe-oryvea-hero.mp4";

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^video\/(mp4|webm|quicktime)$/.test(file.type) && !/\.(mp4|webm|mov)$/i.test(file.name)) {
      toast.error("Format vidéo non supporté (MP4, WebM ou MOV)");
      e.target.value = "";
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Vidéo trop volumineuse (max 8 Mo)");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOver((prev) => ({ ...(prev || {}), heroHome: data.url }));
      toast.success("Vidéo téléversée — cliquez sur Enregistrer pour la publier");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const reset = () => setOver((prev) => { const next = { ...(prev || {}) }; delete next.heroHome; return next; });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/content", { videos: over || {} });
      refresh();
      toast.success("Vidéo d'accueil enregistrée");
    } catch { toast.error("Erreur lors de l'enregistrement de la vidéo"); }
    finally { setBusy(false); }
  };

  if (!over) return <p className="text-sm" style={{ color: "var(--lo-muted)" }}>Chargement…</p>;

  return (
    <div data-testid="admin-content-videos">
      <div className="card-lux p-6 max-w-4xl">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <p className="overline mb-2" style={{ fontSize: "0.6rem" }}>Accueil — Hero</p>
            <h3 className="font-display text-2xl" style={{ fontWeight: 300 }}>Vidéo pour Elle & Pour Lui</h3>
            <p className="text-xs mt-2" style={{ color: "var(--lo-muted)" }}>La vidéo affichée en haut de la page d'accueil. MP4, WebM ou MOV · 8 Mo maximum · lecture automatique sans son.</p>
          </div>
          <div className="flex gap-2">
            {current && <button onClick={reset} className="btn-ghost h-10 px-4 text-xs" disabled={uploading || busy}>Réinitialiser</button>}
            <label className="btn-ghost h-10 px-4 text-xs cursor-pointer whitespace-nowrap">
              {uploading ? "Téléversement…" : "Remplacer la vidéo"}
              <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={onFile} disabled={uploading || busy} />
            </label>
            <button onClick={save} className="btn-primary h-10 px-5 text-xs" disabled={uploading || busy}>{busy ? "…" : "Enregistrer"}</button>
          </div>
        </div>
        <div className="rounded-2xl overflow-hidden border bg-black" style={{ borderColor: "var(--lo-border-rose)" }}>
          <video src={preview} controls muted playsInline loop preload="metadata" className="w-full aspect-video object-cover" />
        </div>
        <p className="text-[11px] mt-4" style={{ color: "var(--lo-muted)" }}>
          {current ? "Vidéo personnalisée active. Vous pouvez la remplacer à tout moment." : "Aucune vidéo personnalisée : la vidéo Luxe Oryvea intégrée au site est utilisée."}
        </p>
      </div>
    </div>
  );
}

export function AdminContent() {
  return (
    <div data-testid="admin-content">
      <Tabs defaultValue="settings">
        <TabsList className="bg-transparent border-b w-full justify-start rounded-none mb-8 h-auto p-0 flex-wrap" style={{ borderColor: "var(--lo-border)" }}>
          {[["settings", "Paramètres"], ["texts", "Textes du site"], ["images", "Images du site"], ["videos", "Vidéos du site"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="overline data-[state=active]:text-[var(--lo-rose)] data-[state=active]:border-b-2 rounded-none pb-3 px-4 bg-transparent" style={{ borderColor: "var(--lo-rose)" }} data-testid={`content-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="settings"><AdminSettings /></TabsContent>
        <TabsContent value="texts"><TextEditor /></TabsContent>
        <TabsContent value="images"><ImageEditor /></TabsContent>
        <TabsContent value="videos"><VideoEditor /></TabsContent>
      </Tabs>
    </div>
  );
}
